import React, { useState } from "react";
import styled from "styled-components";
import { Mountain, LayoutDashboard, User } from "lucide-react";
import UploadDialog from "./UploadDialog";

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  background: #f9fafb;
  font-family: "Inter", sans-serif;

  @media (max-width: 800px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
`;

const Sidebar = styled.div`
  width: 220px;
  background: #fff;
  color: #000;
  display: flex;
  flex-direction: column;
  padding: 24px 0 0 0;
  font-family: "Inter", sans-serif;
  box-shadow: 0 0px 23px rgba(0, 0, 0, 0.05);
  @media (max-width: 800px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const SidebarLogo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  padding: 0 0 32px 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 800px) {
    padding: 0;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 32px;
  @media (max-width: 800px) {
    display: none;
  }
`;

const SidebarNavItem = styled.div`
  font-size: 1rem;
  color: #000;
  padding: 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  &:hover {
    color: rgb(86, 86, 86);
  }
`;

const Main = styled.div`
  flex: 1;
  padding: 40px 40px 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  box-sizing: border-box;

  @media (max-width: 800px) {
    padding: 20px 20px 0 20px;
    height: auto;
  }
`;

const TopBar = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 32px;
  gap: 12px;
  position: sticky;
  top: 0;
  background: #f9fafb;
  z-index: 2;
`;

const RegionSelect = styled.select`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #333;
  font-size: 1rem;
`;

const UploadButton = styled.button`
  background: #23272e;
  color: #fff;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #111318;
  }
`;

const RegionGrid = styled.div`
  width: 100%;
  max-width: 1200px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const RegionCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  padding: 0;
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    border 0.15s;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #b6c0d1;
  }
`;

const RegionCardTop = styled.div`
  width: 100%;
  height: 150px;
  background: #f3f4f6;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url("/lines.png") center center / cover no-repeat;
    opacity: 0.8;
    z-index: 0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const RegionLogo = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const RegionInfo = styled.div`
  padding: 18px 24px 20px 24px;
`;

const RegionName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #23272e;
`;

const RegionLocation = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 1px;
`;

const RegionVisits = styled.div`
  position: absolute;
  top: 16px;
  right: 18px;
  background: #fff;
  color: #23272e;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 4px 14px;
`;

const RegionScrollArea = styled.div`
  width: 100%;
  max-width: 1200px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 15px;
  margin-right: -15px;
  padding-bottom: 40px;
`;

// Dummy region list for now
const regions = ["All", "California", "Colorado", "Utah"];

// Demo data for empty state
const demoRegion = {
  id: "recJvFg2IzTGtH0NY",
  name: "Bay Area",
  location: "California, USA",
  totalVisits: 0,
  isDemo: true,
};

export default function Home({
  regions = [],
  setSelectedRegionId,
  fetchRegions,
  setIsDemo,
}) {
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  console.log("regions", regions);

  // Sort by number of visits (totalVisits), descending
  let filteredRegions = regions.sort((a, b) => {
    const visitsA = a.totalVisits || 0;
    const visitsB = b.totalVisits || 0;
    return visitsB - visitsA;
  });

  // Filter out regions with no visits
  filteredRegions = regions.filter((region) => region.totalVisits > 0);

  // Use demo data if no regions found
  const regionsToDisplay =
    filteredRegions.length > 0 ? filteredRegions : [demoRegion];

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogo>
          <Mountain size={28} style={{ marginRight: -7, marginBottom: 5 }} />
          EveryHike
        </SidebarLogo>
        <SidebarNav>
          <SidebarNavItem>
            <LayoutDashboard size={18} style={{ marginRight: 8 }} />
            Dashboard
          </SidebarNavItem>
          <SidebarNavItem>
            <User size={18} style={{ marginRight: 8 }} />
            Account
          </SidebarNavItem>
        </SidebarNav>
      </Sidebar>
      <Main>
        <TopBar>
          <UploadButton onClick={() => setShowUploadDialog(true)}>
            Upload GPX Files
          </UploadButton>
        </TopBar>
        <RegionScrollArea>
          <RegionGrid>
            {regionsToDisplay.map((region) => (
              <RegionCard
                key={region.id}
                onClick={() => {
                  setIsDemo(region.isDemo);
                  setSelectedRegionId(region.id);
                }}
              >
                <RegionCardTop>
                  <RegionLogo>
                    {region.logo ? (
                      <img src={`/${region.logo}`} alt={region.name} />
                    ) : (
                      <Mountain size={48} color="#bbb" />
                    )}
                  </RegionLogo>
                </RegionCardTop>
                <RegionVisits>
                  {region.isDemo ? "Demo" : `${region.totalVisits || 0} hikes`}
                </RegionVisits>
                <RegionInfo>
                  <RegionName>{region.name}</RegionName>
                  <RegionLocation>{region.location || "\u200B"}</RegionLocation>
                </RegionInfo>
              </RegionCard>
            ))}
          </RegionGrid>
        </RegionScrollArea>
        {showUploadDialog && (
          <UploadDialog
            regions={regions}
            onClose={() => setShowUploadDialog(false)}
            onUpload={fetchRegions}
          />
        )}
      </Main>
    </PageContainer>
  );
}
